import { pageTitle } from '@palaccount/config';
import type { CreditReason } from '@palaccount/contracts';
import { MAX_PAGE_SIZE } from '@palaccount/contracts';
import { Money } from '@palaccount/money';
import { Loader2, Send, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { Amount, Ltr, Pct, Qty } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { DocumentNumberChip } from '@/components/document-number-chip';
import { useUnsavedChanges } from '@/components/document-header-controls';
import { DocumentToolbar } from '@/components/document-toolbar';
import { DocStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { useCashAccountOptions } from '@/features/accounts/api';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { AttachmentsPanel } from '@/features/attachments/attachments-panel';
import { DocumentActions } from '@/features/documents/document-actions';
import { DocumentTotals } from '@/features/documents/document-totals';
import { FxPanel, useRateWarningGate } from '@/features/documents/fx-panel';
import { RateField, useRateInput } from '@/features/documents/rate-field';
import { creditNoteDocument } from '@/features/documents/record-documents';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { formatQty } from '@/lib/format';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import { useItemsHeld } from '@/features/inventory/api';

import { useInvoice } from './api';
import { ApplyCreditNoteDialog, CreditApplicationsCard } from './apply-credit-dialog';
import { StandaloneReturnEditor } from './return-standalone-editor';
import { fitsCap, isReturnEditable, remainingQty, toRequestLines, trimQty } from './return-caps';
import {
  captureBlocks,
  textFromStored,
  trackingPayload,
  type CaptureText,
  type TrackedItem,
} from './tracking-capture';
import { TrackingRows } from './tracking-rows';
import {
  useCreateCreditNote,
  useCreditNote,
  useCreditNotes,
  useDeleteCreditNote,
  usePostCreditNote,
  useRefundCreditNote,
  useUpdateCreditNote,
} from './returns-api';

const REASONS: CreditReason[] = [
  'damaged_goods',
  'wrong_item_shipped',
  'customer_changed_mind',
  'overstock',
  'other',
];

interface ReturnRow {
  invoiceLineNo: number;
  itemId: string | null;
  description: string;
  billedQty: string;
  alreadyReturnedQty: string;
  /**
   * `N-4` — the unit all three cap figures are in. It is the INVOICE LINE's
   * (`BR-MD-03-04d`), which is why it is carried on the row rather than looked
   * up from the item: a line billed in boxes must not read back in kilograms.
   * Null on a free-text line, which has no item and no unit.
   */
  unitSymbol: string | null;
  unitPrecision: number | null;
  unitPrice: string;
  discountPct: string;
  qty: string;
  /**
   * BR-MD-03-17 the other way round. A tracked unit that comes back must say
   * WHICH unit, or it rejoins the shelf as an anonymous +1 and can never be
   * sold again — so the same sub-table the sell side uses sits under the row.
   */
  tracking: CaptureText;
}

/**
 * `N-4` for a cap figure. The unit is the invoice line's, and a FREE-TEXT
 * line has none at all — no item, no unit of measure, and therefore no
 * precision to round to. `formatQty`'s parameter is required precisely so
 * that case has to be answered rather than defaulted, and the honest answer
 * is the bare figure.
 */
function CapQty({ row, value }: { row: ReturnRow; value: string }) {
  if (row.unitPrecision === null) {
    return (
      <bdi dir="ltr" className="font-mono tabular-nums">
        {trimQty(value)}
      </bdi>
    );
  }
  return <Qty value={value} precision={row.unitPrecision} unit={row.unitSymbol ?? ''} />;
}

export function CellInput({ mono, className, ...props }: React.ComponentProps<'input'> & { mono?: boolean }) {
  return (
    <input
      className={cn(
        'h-9 w-full min-w-0 rounded-md border border-transparent bg-transparent px-2 text-sm outline-none transition-colors',
        'hover:border-input focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-70',
        mono && 'text-end font-mono tabular-nums',
        className,
      )}
      {...props}
    />
  );
}

/**
 * SAL-05 — the Sales Return (Credit Note) editor, reached from a POSTED
 * invoice's own screen ("Credit this invoice" — the invoice arrives already
 * linked with its lines loaded, SAL-05 §3) or from the returns list.
 *
 * What the caller controls is exactly what the spec allows: WHICH lines and
 * HOW MANY come back (capped per BR-SAL-05-03, the remainder shown per
 * line), the reason (Other demands a note — BR-SAL-05-08), and the restock
 * flag (BR-SAL-05-07 — stored now, drives stock when it exists). Prices and
 * rates are the invoice's own, drawn read-only (BR-SAL-05-04). Post return
 * (sales.approve) writes the opposite ledger entry; a posted return is
 * locked except its notes (R8).
 *
 * RET-02: a return that starts from NO invoice is the other shape of the same
 * document, drawn by `StandaloneReturnEditor`. The route decides which one
 * below; this component is the invoice-backed editor and is untouched by the
 * stand-alone rules.
 */
export function ReturnEditorPage() {
  const { creditNoteId } = useParams<{ creditNoteId: string }>();
  const [searchParams] = useSearchParams();
  const sourceInvoiceId = searchParams.get('invoiceId') ?? undefined;
  const { data: creditNote, isPending } = useCreditNote(creditNoteId);

  // A new return with no invoice in the address, or a saved note with no
  // invoice behind it, is stand-alone. A saved note decides once it is read.
  if (!creditNoteId) {
    return sourceInvoiceId ? (
      <InvoiceReturnEditor creditNoteId={undefined} sourceInvoiceId={sourceInvoiceId} />
    ) : (
      <StandaloneReturnEditor creditNoteId={undefined} />
    );
  }
  if (isPending || !creditNote) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }
  return creditNote.invoiceId === null ? (
    <StandaloneReturnEditor creditNoteId={creditNoteId} />
  ) : (
    <InvoiceReturnEditor creditNoteId={creditNoteId} sourceInvoiceId={undefined} />
  );
}

function InvoiceReturnEditor({
  creditNoteId,
  sourceInvoiceId,
}: {
  creditNoteId: string | undefined;
  sourceInvoiceId: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  // The reader's language is the PDF's; `print.language` may override it in
  // the template (INV-H6), for the worker and the browser alike.
  const printLocale = i18n.language.startsWith('ar') ? ('ar' as const) : ('en' as const);
  const navigate = useNavigate();
  const isNew = !creditNoteId;

  const { data: creditNote, isPending: loadingNote } = useCreditNote(creditNoteId);
  // A NEW return builds its rows from the posted invoice + its posted returns.
  const { data: sourceInvoice } = useInvoice(isNew ? sourceInvoiceId : undefined);
  /*
   * NOT a list screen — "every posted credit note against THIS invoice",
   * summed to prefill each line's remaining quantity. The page ceiling, not
   * the unbounded `'all'`: BR-SAL-05's cumulative cap is re-checked on post,
   * so the worst a truncation could do is offer a default the server refuses.
   */
  const { data: priorReturnsPage } = useCreditNotes(
    isNew && sourceInvoiceId ? { invoiceId: sourceInvoiceId, status: 'posted' } : {},
    1,
    MAX_PAGE_SIZE,
  );
  const priorReturns = priorReturnsPage?.creditNotes;

  const createCreditNote = useCreateCreditNote();
  const updateCreditNote = useUpdateCreditNote(creditNoteId ?? '');
  const deleteCreditNote = useDeleteCreditNote(creditNoteId ?? '');
  const postCreditNote = usePostCreditNote(creditNoteId ?? '');
  const refundCreditNote = useRefundCreditNote(creditNoteId ?? '');
  /** SAL-05 §12 row 4's cash account — where the refund is paid from. */
  const { options: cashOptions } = useCashAccountOptions();
  const [refundAccountId, setRefundAccountId] = useState('');
  /** Wave 4A — the "Apply to invoices" box is open. */
  const [applying, setApplying] = useState(false);

  const canEdit = useCan(isNew ? 'sales.create' : 'sales.edit');
  const canPost = useCan('sales.approve');
  const canEditNotes = useCan('sales.edit');

  const [creditDate, setCreditDate] = useDocumentDate();
  const [reason, setReason] = useState<CreditReason>('damaged_goods');
  const [reasonNote, setReasonNote] = useState('');
  const [restock, setRestock] = useState(false);
  const [rows, setRows] = useState<ReturnRow[]>([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** BR-MD-11-14's question, in ADM-02 §8's shared box (BR-PERM-15). */
  const { confirmRateWarning, rateWarningDialog } = useRateWarningGate();
  const loadedId = useRef<string | null>(null);
  /** S4 — the header rail's ‹‹ ›› wait while there is typed work (see `useUnsavedChanges`). */
  const dirty = useUnsavedChanges(
    [creditDate, reason, reasonNote, restock, rows, notes],
    isNew ? 'new' : creditNote && loadedId.current === creditNote.id ? creditNote.id : null,
  );
  const seededNew = useRef(false);

  /*
   * The catalogue owns an item's tracking (MD-03 panel 7) and a kit's parts
   * (panel 2) — the credit note carries neither, and R7 says it must not.
   * This is a LOOKUP and never a picker: a return's rows come from the
   * invoice, so it asks for the items THOSE rows name (GEN-50's `ids` read)
   * rather than for a catalogue nobody is choosing from.
   */
  const { data: items } = useItemsHeld(
    useMemo(() => rows.map((row) => row.itemId), [rows]),
  );

  const status = creditNote?.status ?? 'draft';
  const editable = isReturnEditable(status) && canEdit;
  // GEN-108's chain, ending where it should: the DOCUMENT's own code, else
  // the party's usual currency, else the company's base (GEN-107) read from
  // the session — never a literal, which printed dollars on a euro
  // company's every new document until a party was picked.
  const base = useBaseCurrency();
  const currency = creditNote?.currency ?? sourceInvoice?.currency ?? base;
  // Wave 4D — the editable rate (features/documents/rate-field).
  const rate = useRateInput(creditNote);

  useEffect(() => {
    document.title = pageTitle(t('returns.editorTitle'));
  }, [t]);

  // Existing note: adopt the stored document (caps ride each line).
  useEffect(() => {
    if (creditNote && loadedId.current !== creditNote.id) {
      loadedId.current = creditNote.id;
      setCreditDate(creditNote.creditDate);
      setReason(creditNote.reason);
      setReasonNote(creditNote.reasonNote ?? '');
      setRestock(creditNote.restock);
      setNotes(creditNote.notes ?? '');
      setRows(
        creditNote.lines.map((line) => ({
          // Null only on a stand-alone line, which this editor never draws.
          invoiceLineNo: line.invoiceLineNo ?? 0,
          itemId: line.itemId,
          description: line.description,
          billedQty: line.billedQty ?? '0',
          alreadyReturnedQty: line.alreadyReturnedQty ?? '0',
          unitSymbol: line.unitSymbol,
          unitPrecision: line.unitPrecision,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
          qty: trimQty(line.qty),
          tracking: textFromStored(line),
        })),
      );
    }
  }, [creditNote]);

  // New note: the invoice's lines arrive loaded, quantities defaulting to the
  // full remainder (SAL-05 §16: "billed quantity, already-returned quantity,
  // price and tax rate").
  useEffect(() => {
    if (isNew && sourceInvoice && priorReturns && !seededNew.current) {
      seededNew.current = true;
      const returnedByLine = new Map<number, number>();
      for (const prior of priorReturns) {
        for (const line of prior.lines) {
          // A stand-alone note (RET-02) has no billed line to count against.
          if (line.invoiceLineNo === null) continue;
          returnedByLine.set(
            line.invoiceLineNo,
            (returnedByLine.get(line.invoiceLineNo) ?? 0) + Number(line.qty),
          );
        }
      }
      setRows(
        sourceInvoice.lines.map((line) => {
          const already = String(returnedByLine.get(line.lineNo) ?? 0);
          const remaining = remainingQty(
            { billedQty: line.qty, alreadyReturnedQty: already },
            sourceInvoice.currency,
          );
          return {
            invoiceLineNo: line.lineNo,
            itemId: line.itemId,
            description: line.description,
            billedQty: line.qty,
            alreadyReturnedQty: already,
            // SAL-04's own resolution of the same rule — the invoice line
            // already carries the unit its `qty` is written in.
            unitSymbol: line.unitSymbol,
            unitPrecision: line.unitPrecision,
            unitPrice: line.unitPrice,
            discountPct: line.discountPct,
            qty: remaining === '0' ? '' : trimQty(remaining),
            tracking: {},
          };
        }),
      );
    }
  }, [isNew, sourceInvoice, priorReturns]);

  const setRowQty = (invoiceLineNo: number, qty: string) => {
    setRows((current) =>
      current.map((row) => (row.invoiceLineNo === invoiceLineNo ? { ...row, qty } : row)),
    );
  };

  const setRowTracking = (invoiceLineNo: number, key: string, value: string) => {
    setRows((current) =>
      current.map((row) =>
        row.invoiceLineNo === invoiceLineNo
          ? { ...row, tracking: { ...row.tracking, [key]: value } }
          : row,
      ),
    );
  };

  /**
   * What this row must identify coming back — the same blocks the sell side
   * asks for, because the rule belongs to the ITEM, not to the document.
   */
  const blocksFor = (row: ReturnRow) =>
    captureBlocks(
      (items ?? []).find((candidate) => candidate.id === row.itemId) as TrackedItem | undefined,
      row.qty,
    );

  const requestLines = useMemo(
    () =>
      toRequestLines(
        rows.map((row) => ({
          ...row,
          // Damaged goods restock nothing (BR-SAL-05-07), so nothing is named.
          tracking: restock ? trackingPayload(blocksFor(row), row.tracking) : {},
        })),
      ),
    // `blocksFor` closes over `items`, which is why it is in the list.
    [rows, items, restock],
  );

  const invoiceCode = creditNote?.invoiceCode ?? sourceInvoice?.code ?? '';
  const partnerName = creditNote?.partnerName ?? sourceInvoice?.partnerName ?? '';
  const linkedInvoiceId = creditNote?.invoiceId ?? sourceInvoiceId ?? undefined;

  const validate = (): string | null => {
    if (!linkedInvoiceId) return t('returns.errPickInvoice');
    if (requestLines.length === 0) return t('returns.errNoLines');
    for (const row of rows) {
      if (row.qty.trim() === '') continue;
      if (
        !fitsCap(row.qty, { billedQty: row.billedQty, alreadyReturnedQty: row.alreadyReturnedQty }, currency)
      ) {
        return t('returns.errQtyCap', {
          billed: trimQty(row.billedQty),
          name: row.description,
          returned: trimQty(row.alreadyReturnedQty),
          left: remainingQty(
            { billedQty: row.billedQty, alreadyReturnedQty: row.alreadyReturnedQty },
            currency,
          ),
        });
      }
    }
    if (reason === 'other' && reasonNote.trim() === '') return t('returns.errOtherNeedsNote');
    return null;
  };

  const handleFailure = (
    thrown: unknown,
    permission: 'sales.create' | 'sales.edit' | 'sales.delete' | 'sales.approve',
  ) => {
    if (thrown instanceof ApiError && thrown.status === 403) denyPermission(permission);
    // The line above already reads the cause to spot a 403; this one used to
    // throw it away one expression later. `localizeServerCause` keeps it: a
    // refusal keeps the server's sentence localized (`CREDIT_MESSAGES` are
    // mapped in STATIC_MESSAGE_KEYS), a request that never answered gets this
    // screen's own sentence rather than the browser's `Failed to fetch`.
    setError(localizeServerCause(t, thrown, 'returns.errSaveFailed'));
  };

  const save = async (navigateAfter = true): Promise<string | null> => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return null;
    }
    try {
      if (isNew) {
        const created = await createCreditNote.mutateAsync({
          ...rate.createBody(),
          invoiceId: linkedInvoiceId!,
          creditDate,
          reason,
          ...(reasonNote.trim() ? { reasonNote: reasonNote.trim() } : {}),
          restock,
          lines: requestLines,
          ...(notes.trim() ? { notes } : {}),
        });
        if (navigateAfter) void navigate(`/sales/returns/${created.id}`, { replace: true });
        return created.id;
      }
      await updateCreditNote.mutateAsync({
        ...rate.patchBody(),
        creditDate,
        reason,
        reasonNote: reasonNote.trim() === '' ? null : reasonNote.trim(),
        restock,
        lines: requestLines,
        notes: notes.trim() === '' ? null : notes,
      });
      loadedId.current = null;
      return creditNoteId!;
    } catch (thrown) {
      handleFailure(thrown, isNew ? 'sales.create' : 'sales.edit');
      return null;
    }
  };

  const onSave = async () => {
    setBusy(true);
    await save();
    setBusy(false);
  };

  /** Post return (T9's credit-note caption) — saves first, then posts. */
  const onPost = async () => {
    // BR-MD-11-14 at the confirming click, before anything is written.
    if (!(await confirmRateWarning(creditNote?.rateWarning))) return;
    setBusy(true);
    setError(null);
    try {
      const id = await save(false);
      if (!id) return;
      if (isNew) {
        const { api } = await import('@/api');
        const response = await api.creditNotes.post({ params: { creditNoteId: id }, body: {} });
        if (response.status !== 200) {
          if (response.status === 403) denyPermission('sales.approve');
          const body = response.body;
          const sentence =
            typeof body === 'object' && body !== null && 'message' in body
              ? String((body as { message: unknown }).message)
              : t('common.loadFailed');
          // D46: the restock door raises the stock family (serialsRequired,
          // serialAlreadyInStock, batchRequired…), so the body is lifted into
          // an ApiError and the seam prefers its key+params.
          setError(localizeServerMessage(t, sentence, new ApiError(response.status, sentence, body)));
          return;
        }
        void navigate(`/sales/returns/${id}`, { replace: true });
        return;
      }
      await postCreditNote.mutateAsync({});
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'sales.approve');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setDeleting(false);
    if (!creditNoteId) return;
    setBusy(true);
    setError(null);
    try {
      await deleteCreditNote.mutateAsync();
      void navigate('/sales/returns');
    } catch (thrown) {
      handleFailure(thrown, 'sales.delete');
    } finally {
      setBusy(false);
    }
  };

  const onSaveNotes = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateCreditNote.mutateAsync({ notes: notes.trim() === '' ? null : notes });
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'sales.edit');
    } finally {
      setBusy(false);
    }
  };

  /** SAL-05 §15 Q3's Refund action — the credit leaves as cash, once. */
  const onRefund = async () => {
    if (!creditNoteId || !refundAccountId) return;
    setBusy(true);
    setError(null);
    try {
      await refundCreditNote.mutateAsync(refundAccountId);
      loadedId.current = null;
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403)
        denyPermission('accounting.approve');
      setError(localizeServerCause(t, thrown, 'returns.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loadingNote) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  // The live credit figures ride the SERVER for an existing draft (its stored
  // block is authoritative); a new return shows the invoice figures per line.
  const storedTotals = creditNote ?? null;

  return (
    <div className="grid gap-4">
      {/* BR-MD-11-14's question in ADM-02 §8's shared box. Rendered
          UNCONDITIONALLY, unlike the delete box below: this one is awaited, so
          a dialog that unmounted mid-question would leave the post hanging with
          no error anywhere. See `useRateWarningGate`. */}
      {rateWarningDialog}
      {/* ── Toolbar: the CN number in the number field (BR-SAL-05-09) ────── */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('returns.editorTitle')}</h2>
        {/* BR-SAL-00-02's naming half, on the shared chip. This screen's fixed
            label is "Credit Note No." — the spec's own synonym for the sales
            return (§6.3, the CN- series); the deviation from the rule's
            literal "Return No." is recorded at the chip component. */}
        <DocumentNumberChip
          label={t('returns.colNo')}
          code={creditNote?.code ?? null}
          fallback={t('returns.codeNew')}
        />
        {/* BR-SAL-00-26, on the second of the five it governs — the rule
            applies to each screen in its own right, not once to the shared
            layout, so the sales return carries its own unconditional chip. */}
        <DocStatusChip status={status} />
        {/* The strip's source pill: "Against invoice · INV-####" — clicks back. */}
        {invoiceCode && linkedInvoiceId ? (
          <Link
            to={`/sales/${linkedInvoiceId}`}
            className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            {t('returns.againstInvoice', { code: invoiceCode })}
          </Link>
        ) : null}
        {/* The toolbar's acts, DECLARED — see `document-toolbar.tsx`. */}
        <DocumentToolbar
          header={{ kind: 'creditNote', id: creditNoteId, dirty }}
          actions={[
            {
              // Not PART 4.6's Cancel row: a link back to the list wearing the
              // same word. The primitive's header records the collision.
              id: 'back',
              row: null,
              label: t('common.cancel'),
              variant: 'outline',
              to: '/sales/returns',
            },
            {
              // Wave 2 — the invoice's Print/PDF pair on the credit note.
              // Saved notes only.
              id: 'printPdf',
              row: 'print',
              node:
                !isNew && creditNote ? (
                  <DocumentActions
                    request={{ kind: 'credit_note', locale: printLocale, subjectId: creditNote.id }}
                    payload={{
                      kind: 'credit_note',
                      data: creditNoteDocument(
                        creditNote,
                        (itemId) => items?.find((item) => item.id === itemId)?.code ?? null,
                      ),
                    }}
                  />
                ) : null,
            },
            {
              // The document's question and the role's, kept apart (16.7).
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && status === 'draft',
              gate: { permission: 'sales.delete', reasonKey: 'perm.cannot.deleteSalesReturns' },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              // Wave 4A — set the posted credit against the customer's open
              // invoices. Offered while unapplied credit stands and the
              // note was not refunded; the same hand that may post decides.
              id: 'apply',
              row: null,
              label: t('returns.applyToInvoices'),
              variant: 'outline',
              visible:
                !isNew &&
                status === 'posted' &&
                creditNote !== undefined &&
                creditNote.refundedAt === null &&
                Money.of(creditNote.unappliedTotal, creditNote.currency).isPositive(),
              gate: { permission: 'sales.approve', reasonKey: 'perm.cannot.postSalesReturns' },
              disabled: busy,
              onAct: () => setApplying(true),
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              visible: isReturnEditable(status),
              gate: {
                permission: isNew ? 'sales.create' : 'sales.edit',
                reasonKey: isNew
                  ? 'perm.cannot.createSalesReturns'
                  : 'perm.cannot.editSalesReturns',
              },
              busy,
              onAct: () => void onSave(),
            },
          ]}
        >
          {creditNote ? (
            <ApplyCreditNoteDialog creditNote={creditNote} open={applying} onClose={() => setApplying(false)} />
          ) : null}
          {/* GEN-58 asks its sentence in the product’s ONE shared box
              (GEN-204, GEN-209 — "deleting a draft" is first on its list).
              `window.confirm` was neither of BR-PERM-15’s two patterns.
              Inside the same guard as the button, so the dialog cannot
              outlive the condition that allows the delete. */}
          {!isNew && status === 'draft' ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('returns.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
        </DocumentToolbar>
      </div>

      {status === 'posted' ? (
        <p className="rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          {t('returns.postedNotice')}
        </p>
      ) : null}
      {/* SAL-05 §15 Q3: "off the balance, with an explicit REFUND action
          for cash" — offered while the posted credit still stands; gone
          once refunded (once ever). Money out is the treasurer's act. */}
      {status === 'posted' && !creditNote?.refundedAt ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          <span className="me-auto">{t('returns.refundHint')}</span>
          <NativeSelect
            aria-label={t('returns.refundAccount')}
            value={refundAccountId}
            onChange={(event) => setRefundAccountId(event.target.value)}
            className="w-auto"
          >
            <option value="">{t('receipts.pickAccount')}</option>
            {cashOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.code} — {option.name}
              </option>
            ))}
          </NativeSelect>
          <Gated permission="accounting.approve" reasonKey="perm.cannot.refundCreditNotes">
            <Button size="sm" variant="outline" onClick={() => void onRefund()} disabled={busy || !refundAccountId}>
              {t('returns.refund')}
            </Button>
          </Gated>
        </div>
      ) : null}
      {creditNote?.refundedAt ? (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {t('returns.refundedNotice')}
        </p>
      ) : null}
      {/* Wave 4A: the full credit is applied — nothing left to refund or apply. */}
      {status === 'credited' ? (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {t('returns.creditedNotice')}
        </p>
      ) : null}
      {creditNote ? <CreditApplicationsCard creditNote={creditNote} /> : null}
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}

      {/* ── The details line grown by two fields (SAL-05 §4) ─────────────── */}
      <Card>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="grid gap-1.5">
            <Label htmlFor="cn-date">{t('common.date')}</Label>
            <Input
              id="cn-date"
              type="date"
              dir="ltr"
              value={creditDate}
              onChange={(event) => setCreditDate(event.target.value)}
              disabled={!editable}
            />
          </div>
          {/* Wave 4D — the editable rate (features/documents/rate-field). */}
          <RateField idPrefix="cn" currency={currency} doc={creditNote} input={rate} disabled={!editable} />
          <div className="grid gap-1.5">
            <Label>{t('returns.fAgainst')}</Label>
            <div className="flex h-12 items-center gap-3 rounded-[11px] border border-input bg-muted/40 px-3 font-mono text-sm">
              <Ltr value={invoiceCode || '—'} />
              <span className="ms-auto truncate text-muted-foreground">{partnerName}</span>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="cn-reason">{t('returns.fReason')}</Label>
            <NativeSelect
              id="cn-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value as CreditReason)}
              disabled={!editable}
            >
              {REASONS.map((value) => (
                <option key={value} value={value}>
                  {t(`returns.reason.${value}`)}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1.5">
            {/* BR-SAL-05-07: the restock switch — REAL: posting writes the
                return movement at the original out cost and reverses COGS
                (SAL-05 §12). Damaged goods: leave it off.

                ERR-SAL-05-06 is the §14 row for this control, and it is one
                of the rows that describes the PROTOTYPE rather than a
                behaviour to build: "looked for a restock switch — (there is
                none) — this is the gap in BR-SAL-05-07". It is cited HERE
                because this is the exact act it names — a user on the return
                screen looking for the switch — and the answer is no longer
                "there is none": the control below is it, `restock` rides the
                create and update payloads, and `credit_notes.restock` is
                what `restockGoods` reads when the note posts. The row's own
                third column delegates to BR-SAL-05-07, which is cited on the
                service side, so the two say the same thing about the same
                field. Nothing in the row is enforced as a MESSAGE, because
                it has none — the wording difference is that the row records
                an absence and the build supplies the thing. */}
            <Label htmlFor="cn-restock">{t('returns.fRestock')}</Label>
            <label className="flex h-12 items-center gap-3 rounded-[11px] border border-input px-3 text-sm">
              <Checkbox
                id="cn-restock"
                checked={restock}
                onCheckedChange={(checked) => setRestock(checked === true)}
                disabled={!editable}
              />
              <span className="text-muted-foreground">{t('returns.restockHint')}</span>
            </label>
          </div>
          {reason === 'other' ? (
            <div className="grid gap-1.5 sm:col-span-2 lg:col-span-4">
              {/* BR-SAL-05-08. */}
              <Label htmlFor="cn-reason-note">{t('returns.fReasonNote')}</Label>
              <Input
                id="cn-reason-note"
                value={reasonNote}
                maxLength={500}
                placeholder={t('returns.reasonNotePh')}
                onChange={(event) => setReasonNote(event.target.value)}
                disabled={!editable}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* ── The returning grid: caps per line, prices read-only ──────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('returns.linesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-11 text-center">#</TableHead>
                <TableHead className="min-w-[220px]">{t('sales.colDescription')}</TableHead>
                <TableHead className="w-24 text-end">{t('returns.colBilled')}</TableHead>
                <TableHead className="w-28 text-end">{t('returns.colReturned')}</TableHead>
                <TableHead className="w-28 text-end">{t('returns.colReturnNow')}</TableHead>
                <TableHead className="w-32 text-end">{t('sales.colUnitPrice')}</TableHead>
                <TableHead className="w-24 text-end">{t('sales.colDisc')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, index) => {
                const remaining = remainingQty(
                  { billedQty: row.billedQty, alreadyReturnedQty: row.alreadyReturnedQty },
                  currency,
                );
                const overCap =
                  row.qty.trim() !== '' &&
                  !fitsCap(
                    row.qty,
                    { billedQty: row.billedQty, alreadyReturnedQty: row.alreadyReturnedQty },
                    currency,
                  );
                return (
                  <React.Fragment key={row.invoiceLineNo}>
                  <TableRow className="odd:bg-muted/30">
                    <TableCell className="text-center font-mono text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell className="font-medium">{row.description}</TableCell>
                    {/* `N-4` on SAL-05 §16's two caps. They were bare trimmed
                        strings — "2" against a line billed in 2.000 kg, and
                        in a grid whose rows may be kilograms and boxes at
                        once (`N-14`). The unit is the INVOICE LINE's, not the
                        item's stock unit (`BR-MD-03-04d`). */}
                    <TableCell className="text-end">
                      <CapQty row={row} value={row.billedQty} />
                    </TableCell>
                    <TableCell className="text-end text-muted-foreground">
                      <CapQty row={row} value={row.alreadyReturnedQty} />
                    </TableCell>
                    <TableCell className={editable ? undefined : 'text-end'}>
                      {/* A POSTED credit note is locked ("only the notes may
                          change"), so its quantity box can never be typed into
                          again — a display wearing an input's clothes, and the
                          same call the delivery note and the voucher make. A
                          DRAFT row keeps its box even at a zero remainder:
                          that box is disabled, not finished. */}
                      {editable ? (
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('returns.cellReturnNow', { line: index + 1 })}
                          className={cn(overCap && 'border-destructive text-destructive')}
                          value={row.qty}
                          placeholder="0"
                          onChange={(event) => setRowQty(row.invoiceLineNo, event.target.value)}
                          disabled={remaining === '0'}
                        />
                      ) : (
                        <CapQty row={row} value={row.qty === '' ? '0' : row.qty} />
                      )}
                      {/* BR-SAL-05-03's remainder, always visible per line.
                          The decimals, not a second symbol: it sits in the
                          cell immediately after the Returned column, which
                          already prints the unit — INV-06's balance call. */}
                      <div className="pe-2 text-end text-[11px] text-muted-foreground">
                        {t('returns.maxMore', {
                          qty:
                            row.unitPrecision === null
                              ? remaining
                              : formatQty(remaining, row.unitPrecision),
                        })}
                      </div>
                    </TableCell>
                    {/* BR-SAL-05-04: copied and read-only. */}
                    <TableCell className="text-end">
                      <Amount value={row.unitPrice} currency={currency} />
                    </TableCell>
                    <TableCell className="text-end font-mono text-muted-foreground">
                      <Pct value={row.discountPct.replace(/\.00$/, '')} />
                    </TableCell>
                  </TableRow>
                  {/* Only when the goods are going back on the shelf:
                      BR-SAL-05-07's damaged goods restock nothing, so there
                      is nothing to identify. */}
                  {restock ? (
                    <TrackingRows
                      blocks={blocksFor(row)}
                      text={row.tracking}
                      onChange={(key, value) => setRowTracking(row.invoiceLineNo, key, value)}
                      disabled={!editable}
                      columns={7}
                      line={index + 1}
                    />
                  ) : null}
                  </React.Fragment>
                );
              })}
            </TableBody>
            {storedTotals ? (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={7} className="text-end text-sm">
                    <span className="text-muted-foreground">{t('orders.netTaxTotal')}</span>{' '}
                    <Amount value={storedTotals.subtotal} currency={currency} /> {' / '}
                    <Amount value={storedTotals.taxAmount} currency={currency} /> {' / '}
                    <Amount
                      value={storedTotals.grandTotal}
                      currency={currency}
                      className="font-bold"
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            ) : null}
          </Table>
          <p className="mt-2 text-xs text-muted-foreground">{t('returns.pricesCopiedHint')}</p>
        </CardContent>
      </Card>

      {/* ── Notes + the posting rail ─────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader>
            <CardTitle>{t('sales.notes')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <Textarea
              aria-label={t('sales.notes')}
              maxLength={2000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={!editable && !(status === 'posted' && canEditNotes)}
            />
            {status === 'posted' ? (
              <Gated
                permission="sales.edit"
                reasonKey="perm.cannot.editNotes"
                className="justify-self-start"
              >
                <Button variant="outline" onClick={() => void onSaveNotes()} disabled={busy}>
                  {t('sales.saveNotes')}
                </Button>
              </Gated>
            ) : null}
          </CardContent>
        </Card>

        <Card className="self-start">
          <CardContent className="grid gap-3">
            {storedTotals ? (
              <>
                {/* Wave 4C — the shared totals rows, from the stored block. */}
                <DocumentTotals
                  currency={currency}
                  subtotal={storedTotals.subtotal}
                  discountAmount={storedTotals.discountAmount}
                  taxAmount={storedTotals.taxAmount}
                  grandTotal={storedTotals.grandTotal}
                  totalLabel={t('returns.creditTotal')}
                />
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{t('returns.totalsOnSave')}</p>
            )}

            {/* SAL-05 §12: the credit note keeps its OWN rate, frozen when it
                is posted — not the rate of the invoice it credits, which may
                be months old. Hidden entirely for a base-currency credit note
                (GEN-116). */}
            <FxPanel doc={creditNote} />

            {/* T9 — "Post return", behind sales.approve. */}
            {status === 'draft' ? (
              <Gated
                permission="sales.approve"
                reasonKey="perm.cannot.postSalesReturns"
                className="w-full"
              >
                <Button
                  className="h-[46px] w-full bg-emerald-700 text-white hover:bg-emerald-800"
                  onClick={() => void onPost()}
                  disabled={busy || requestLines.length === 0}
                >
                  {busy ? <Loader2 className="animate-spin" aria-hidden /> : <Send aria-hidden />}
                  {t('returns.postReturn')}
                </Button>
              </Gated>
            ) : null}
            {/* SPEC-VERSUS-BUILD DEVIATION, carrying NO rule identifier for
                the same reason as FIN-03's: SAL-05 has no approval rule and no
                approval row in its §14 table. The banner is PART 16's
                product-wide pattern applied to a screen the spec never gave
                one, so there is nothing true to cite.

                DO NOT MERGE THIS WITH `PURCHASE_RETURN_MESSAGES.approvalBanner`.
                That constant is PUR-04's — a DIFFERENT return screen — and it
                carries a sentence the specification quotes word for word
                (ERR-PUR-04-05). Two screens, two constants, one of them
                spec-mandated and one of them not. They read almost alike,
                which is exactly why this is written down. */}
            {status === 'draft' && !canPost ? (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('returns.approvalBanner')}
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
      {/* Wave 2 — INV-H5's files panel on this document. Hidden
          behind "save first" on a new draft; permission follows the record. */}
      <AttachmentsPanel kind="credit_note" entityId={creditNoteId} />
    </div>
  );
}
